import type { Api } from '../../api/models/apis';
import { computePropertyGroupPatch } from '../../features/visual-analytics/actions/steps-definitions/dbActionStepTypeAddPropertyGroupAsColumn';
import { computeCalculatedColumnPatchFromFrozenSelection } from '../../features/visual-analytics/actions/steps-definitions/dbActionStepTypeCalculateColumn';
import { computeTableJoinPatches } from '../../features/visual-analytics/actions/steps-definitions/dbActionStepTypeTableJoin';
import { applySnapshotToDataContext } from '../selection/snapshot/dataContextSelectionSnapshot';
import { validateAndPrepareSnapshot } from '../selection/snapshot/selectionFilterSerialization.deserialize';
import type { TpSelectionStoragePayloadV1 } from '../selection/snapshot/selectionFilterSerialization.schemas';
import { applyDerivedColumnPatches, createShadowDataContext, validateLedgerActionAgainstPatches } from './helpers';
import type {
  AddColumnAsync,
  AddColumnFromExpression,
  AddColumnFromPropertyGroups,
  AddColumnTableJoin,
  LedgerAction,
} from './schemas';
import { checkLedgerPreflight } from './preflightValidator';
import { computeAsyncColumnPatch } from './asyncColumnFetch';
import type { TpDataContext } from '../interfaces';

export async function replayDashboardState(
  targetDataContext: TpDataContext,
  ledgerActions: LedgerAction[],
  selectionSnapshot: TpSelectionStoragePayloadV1,
  api: Api,
): Promise<{ shadow: TpDataContext; warnings: string[] }> {
  const { errors } = checkLedgerPreflight(targetDataContext, ledgerActions);
  if (errors.length > 0) throw new Error(errors.join('\n'));

  const shadow = createShadowDataContext(targetDataContext);
  const warnings: string[] = [];

  for (let actionIndex = 0; actionIndex < ledgerActions.length; actionIndex++) {
    const action = ledgerActions[actionIndex];

    try {
      if (action.type === 'DELETE_COLUMN') {
        throw new Error('DELETE_COLUMN replay is not supported');
      }

      const targetColumnId = action.targetColumnIds[0];
      let patches;

      switch (action.payload.type) {
        case 'TABLE_JOIN':
          {
            const result = computeTableJoinPatches(shadow, action.payload as AddColumnTableJoin);
            patches = result.patches;
            warnings.push(...result.warnings);
          }
          break;
        case 'PROPERTY_GROUP_COLUMN':
          patches = [
            computePropertyGroupPatch(shadow, {
              ...(action.payload as AddColumnFromPropertyGroups),
              targetTableId: action.targetTableId,
              targetColumnId,
            }),
          ];
          break;
        case 'EXPRESSION': {
          const result = computeCalculatedColumnPatchFromFrozenSelection(shadow, {
            ...(action.payload as AddColumnFromExpression),
            targetTableId: action.targetTableId,
            targetColumnId,
          });
          patches = [result.patch];
          warnings.push(...result.warnings);
          break;
        }
        case 'ASYNC_COLUMN':
          patches = [
            await computeAsyncColumnPatch(shadow, api, targetDataContext.id, {
              ...(action.payload as AddColumnAsync),
              targetTableId: action.targetTableId,
              targetColumnId,
            }),
          ];
          break;
      }

      validateLedgerActionAgainstPatches(action, patches);
      applyDerivedColumnPatches(shadow, patches, action);
    } catch (error) {
      const reason = error instanceof Error ? error.message : String(error);
      if (action.type !== 'DELETE_COLUMN' && action.payload.type === 'ASYNC_COLUMN') {
        warnings.push(
          `Action ${actionIndex} (ASYNC_COLUMN) skipped column ${action.targetColumnIds.join(', ')}: ${reason}`,
        );
        continue;
      }
      const actionType = action.type === 'DELETE_COLUMN' ? action.type : action.payload.type;
      throw new Error(`Failed to replay action ${actionIndex} (${actionType}): ${reason}`);
    }
  }

  const selectionPlan = validateAndPrepareSnapshot(shadow, selectionSnapshot);
  warnings.push(...selectionPlan.warnings);
  warnings.push(...Object.values(selectionPlan.canonicalTypeSelections).flatMap((selection) => selection.warnings));
  applySnapshotToDataContext(shadow, selectionPlan);

  return { shadow, warnings };
}
